import React, { useEffect, useState, useMemo, Fragment } from 'react'
import injectSheet from 'react-jss'
import {
  groupSelectorStyles,
  groupsFilterStyles,
  selectorStyles
} from './pupil-selector.styles'
import Spinner from 'pobble-components/dist/spinner'
import { useMe } from 'pobble-components/dist/pobble-user'
import { errorHandler } from 'pobble-components/dist/error-handler'
import { Table, TableRow, HeadCell, Cell } from 'pobble-components/dist/table'
import AgeGroup from 'pobble-components/dist/age-group'
import Button from 'pobble-components/dist/button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch'
import { PobbleAvatar } from 'pobble-components/dist/pobble-avatar'
import FormInput from 'pobble-components/dist/form-elements/form-input'
import orderBy from 'lodash/orderBy'
import {
  GroupsSelectionProvider,
  GroupsSelection
} from 'pobble-components/dist/groups-selection'
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter
} from 'pobble-components/dist/modal'
import CircleStatus from 'pobble-components/dist/circle-status'
import {
  BulkActionProvider,
  SelectAllButton,
  BulkCheckbox,
  useBulkAction
} from 'pobble-components/dist/bulk-action'
import NoItemsPlaceholder from 'pobble-components/dist/no-items-placeholder'
import { apiClient } from 'pobble-components/dist/api'
import { URLS } from 'pobble-components/dist/setup'
import Cookies from 'js-cookie'

const apiGetSchoolData = (id, params) =>
  apiClient.get(`v3/schools/${id}`, { params: params })

const GroupsSelector = injectSheet(groupSelectorStyles)(
  ({ classes, onFilter, requestClose, selectedGroups, data }) => {
    return (
      <GroupsSelectionProvider
        selected={selectedGroups}
        groups={data && data.groups}
        categories={data && data.group_categories}>
        {store => (
          <Fragment>
            <ModalContent legacy className={classes.content}>
              {data && <GroupsSelection />}
              {!data && (
                <div className={classes.pending}>
                  <Spinner />
                </div>
              )}
            </ModalContent>
            <ModalFooter alignRight childrenGap>
              <Button tall simple action={requestClose}>
                Cancel
              </Button>
              <Button
                tall
                action={() => {
                  requestClose()
                  onFilter(store.selectedGroups)
                }}>
                Select groups
              </Button>
            </ModalFooter>
          </Fragment>
        )}
      </GroupsSelectionProvider>
    )
  }
)

const GroupsFilter = injectSheet(groupsFilterStyles)(
  ({ selectedGroups, onFilter, classes }) => {
    const [modalOpen, setModalOpen] = useState(false)

    const selectedGroupsLength = Object.keys(selectedGroups).length

    const { me } = useMe()

    const school = me.account.teacher_type.school

    const [data, setData] = useState(null)

    const groupsMap = useMemo(() => {
      if (!data) return {}
      const result = {}
      data.groups.forEach(group => (result[group.id] = group))
      return result
    }, [data])

    const getGroupNames = groupIds => {
      if (!data) return ''
      const result = groupIds.map(id => {
        return groupsMap[id].title
      })
      return result.join(', ')
    }

    const selectedGroupsNames = useMemo(() => {
      return getGroupNames(Object.keys(selectedGroups))
    }, [selectedGroups, data])

    const lastChoice = useMemo(() => {
      if (!data) return null
      const cookie = Cookies.get('pupilsSelectorSelectedGroups')
      if (!cookie) return null
      const groupIds = JSON.parse(cookie)
      const groups = groupIds.map(id => groupsMap[id])
      const names = getGroupNames(groupIds)
      return {
        groups,
        names
      }
    }, [data, selectedGroups])

    useEffect(() => {
      apiGetSchoolData(school.id, {
        include:
          'school.groups,school.group.names,school.group.visibility,school.group.counters,school.group.category,school.group_categories'
      })
        .catch(errorHandler)
        .then(res => setData(res.data.school))
    }, [school.id])

    return (
      <Fragment>
        <div className={classes.container}>
          {!selectedGroupsLength && (
            <Fragment>
              <Button action={() => setModalOpen(true)} simple bleed>
                Select groups
              </Button>
              {lastChoice && (
                <span className={classes.lastChoice}>
                  You last chose:
                  <Button
                    action={() => {
                      const ids = {}
                      lastChoice.groups.forEach(group => (ids[group.id] = true))
                      onFilter(ids)
                    }}
                    simple
                    bleed>
                    {lastChoice.names}
                  </Button>
                </span>
              )}
            </Fragment>
          )}
          {!!selectedGroupsLength && (
            <ul className={classes.groupsInfo}>
              <li>
                <strong>{selectedGroupsNames}</strong>
              </li>
              <li>
                <Button simple bleed action={() => setModalOpen(true)}>
                  Edit
                </Button>
              </li>
              <li>
                <Button simple bleed action={() => onFilter({})}>
                  Reset
                </Button>
              </li>
            </ul>
          )}
        </div>
        <Modal
          isOpen={modalOpen}
          closeButton
          requestClose={() => setModalOpen(false)}>
          <ModalHeader>Select groups</ModalHeader>
          <GroupsSelector
            data={data}
            selectedGroups={selectedGroups}
            requestClose={() => setModalOpen(false)}
            onFilter={groups => {
              Cookies.set('pupilsSelectorSelectedGroups', Object.keys(groups), {
                expires: 7
              })
              onFilter(groups)
            }}
          />
        </Modal>
      </Fragment>
    )
  }
)

const PupilSelector = injectSheet(selectorStyles)(
  ({ isOpen, classes, onSelect, requestClose, preSelected, exclude = {} }) => {
    const { me } = useMe()
    const school = me.account.teacher_type.school
    const [pupils, setPupils] = useState(null)
    const [searchString, setSearchString] = useState('')
    const [sort, setSort] = useState({ key: 'name', direction: 'asc' })
    const [selectedGroups, setSelectedGroups] = useState({})
    const [selected, setSelected] = useState(preSelected || {})

    const selectPupilsInGroups = groups => {
      const selected = {}
      pupils.forEach(pupil => {
        if (!!pupil.groups.filter(group => groups[group.id]).length) {
          selected[pupil.account.id] = true
        }
      })
      setSelected(selected)
    }

    useEffect(() => {
      isOpen &&
        apiGetSchoolData(school.id, {
          include:
            'school.student_types,school.student_type.account.names,school.student_type.year_group.order,school.student_type.year_group.names,school.student_type.groups,school.student_type.group.names,school.student_type.account.avatar,school.student_type.policy'
        })
          .catch(errorHandler)
          .then(res => {
            const students = res.data.school.student_types
            if (Object.keys(exclude).length === 0) {
              setPupils(students)
            } else {
              setPupils(
                students.filter(student => !exclude[student.account.id])
              )
            }
          })
    }, [isOpen])

    const pupilsFilteredByGroups = useMemo(() => {
      if (!pupils) return null
      const groupIds = Object.keys(selectedGroups)
      if (!groupIds.length) return pupils
      return pupils.filter(pupil => {
        return !!pupil.groups.filter(group => selectedGroups[group.id]).length
      })
    }, [pupils, selectedGroups])

    const pupilsFilteredBySearch = useMemo(() => {
      if (!pupilsFilteredByGroups) return null
      return pupilsFilteredByGroups.filter(
        pupil =>
          pupil.account.full_name
            .toLowerCase()
            .indexOf(searchString.toLowerCase()) !== -1
      )
    }, [pupilsFilteredByGroups, searchString])

    const sortedPupils = useMemo(() => {
      if (!pupilsFilteredBySearch) return null
      return sort.key === 'name'
        ? orderBy(pupilsFilteredBySearch, pupil => pupil.account.full_name, [
            sort.direction
          ])
        : sort.key === 'age'
        ? orderBy(pupilsFilteredBySearch, pupil => pupil.year_group.order, [
            sort.direction
          ])
        : pupilsFilteredBySearch
    }, [pupilsFilteredBySearch, sort])

    return (
      <BulkActionProvider
        selected={selected}
        onChange={setSelected}
        preSelected={preSelected}
        entities={sortedPupils}
        idSelector={entity => entity.account.id}>
        <ModalContent legacy className={classes.content}>
          <div className={classes.container}>
            <div className={classes.filters}>
              <GroupsFilter
                onFilter={groups => {
                  setSelectedGroups(groups)
                  selectPupilsInGroups(groups)
                }}
                selectedGroups={selectedGroups}
              />
              <div className={classes.controls}>
                <div className={classes.search}>
                  <FormInput
                    value={searchString}
                    onChange={e => setSearchString(e.target.value)}
                    id="search"
                    placeholder="Type a pupil's name"
                  />
                  <label htmlFor="search">
                    <Button>
                      <FontAwesomeIcon icon={faSearch} />
                    </Button>
                  </label>
                </div>
              </div>
            </div>
            <div className={classes.pupils}>
              {!pupils && (
                <div className={classes.pending}>
                  <Spinner />
                </div>
              )}
              {!!pupils && !sortedPupils.length && (
                <NoItemsPlaceholder className={classes.noItems}>
                  {!!pupils.length ? (
                    <Fragment>
                      <p>
                        We searched everywhere
                        <br />
                        There are no pupils matching this criteria
                      </p>
                      <Button
                        tall
                        action={() => {
                          setSelectedGroups({})
                          setSearchString('')
                        }}
                        simple>
                        Clear filters
                      </Button>
                    </Fragment>
                  ) : (
                    <Fragment>
                      <p>You don’t have any pupils in your school yet</p>
                      <Button simple tall href={`${URLS.DASHBOARD}/add-pupils`}>
                        Add pupils
                      </Button>
                    </Fragment>
                  )}
                </NoItemsPlaceholder>
              )}
              {!!pupils && !!sortedPupils.length && (
                <Table className={classes.table}>
                  <thead>
                    <TableRow>
                      <HeadCell className={classes.allHCell}>
                        <SelectAllButton />
                      </HeadCell>
                      <HeadCell
                        left
                        ordered={sort.key === 'name' && sort.direction}
                        order={direction =>
                          setSort({ key: 'name', direction })
                        }>
                        Name
                      </HeadCell>
                      <HeadCell
                        className={classes.yearHCell}
                        ordered={sort.key === 'age' && sort.direction}
                        order={direction => setSort({ key: 'age', direction })}>
                        Year
                      </HeadCell>
                      <HeadCell className={classes.permissionHCell}>
                        Permission
                      </HeadCell>
                    </TableRow>
                  </thead>
                  <tbody>
                    {sortedPupils.map(pupil => {
                      return (
                        <TableRow key={pupil.account.id}>
                          <Cell center>
                            <BulkCheckbox id={pupil.account.id} />
                          </Cell>
                          <Cell>
                            <div className={classes.pupilName}>
                              <PobbleAvatar
                                className={classes.avatar}
                                avatar={pupil.account.avatar}
                                name={pupil.account.full_name}
                              />
                              {pupil.account.full_name}
                            </div>
                          </Cell>
                          <Cell center className={classes.yearGroupCell}>
                            <AgeGroup data={pupil.year_group} />
                          </Cell>
                          <Cell center>
                            <CircleStatus status={pupil.policy.can_publish} />
                          </Cell>
                        </TableRow>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </ModalContent>
        <PupilSelectorFooter onClose={requestClose} onSelect={onSelect} />
      </BulkActionProvider>
    )
  }
)

const PupilSelectorFooter = ({ onSelect, onClose }) => {
  const { bulkSelectedIds } = useBulkAction()
  return (
    <ModalFooter alignRight childrenGap>
      <Button tall simple action={onClose}>
        {' '}
        Cancel
      </Button>
      <Button
        tall
        disabled={!bulkSelectedIds.length}
        action={() => onSelect(bulkSelectedIds)}>
        Select {!!bulkSelectedIds.length && bulkSelectedIds.length} pupil
        {bulkSelectedIds.length !== 1 && 's'}
      </Button>
    </ModalFooter>
  )
}

// eslint-disable-next-line no-unused-vars
const PupilSelectorModal = ({ classes, onSelect, ...props }) => {
  return (
    <Modal closeButton {...props}>
      <ModalHeader>Select pupils</ModalHeader>
      <PupilSelector
        {...props}
        onSelect={ids => {
          props.requestClose()
          onSelect(ids)
        }}
      />
    </Modal>
  )
}

export default PupilSelectorModal
